// Sesión: cuenta de Firebase Auth, perfil (usuarios/{uid}) y membresías en conjuntos.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onIdTokenChanged, signOut, type User } from "firebase/auth";
import { clearIndexedDbPersistence, collectionGroup, query, terminate, where } from "firebase/firestore";
import type { Miembro, Rol, Usuario } from "@porteria/core";
import { auth, db } from "./firebase";
import { useConsulta, useDocumento, type ConId } from "./datos";
import { desactivarPush } from "./notificaciones";

interface Sesion {
  /** undefined mientras Firebase revisa si hay sesión. */
  cuenta: User | null | undefined;
  perfil: ConId<Usuario> | null | undefined;
  membresias: ConId<Miembro>[] | undefined;
  /** undefined mientras se leen los permisos de la cuenta (claims del token). */
  esSuper: boolean | undefined;
  salir: () => Promise<void>;
}

const Contexto = createContext<Sesion | null>(null);

/**
 * Cierra la sesión sin dejar rastro en el celular: deja de recibir sus avisos y borra la copia local de
 * Firestore (miembros con documento, celulares…). Nada de esto puede impedir que la sesión se cierre.
 */
async function cerrarSesion(uid: string | null) {
  if (uid) {
    try {
      await desactivarPush(uid);
    } catch (e) {
      console.warn("No se pudo desactivar el aviso de este celular", e);
    }
  }
  await signOut(auth);
  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
  } catch (e) {
    // Con la app abierta en otra pestaña la caché sigue en uso; se limpia la próxima vez.
    console.warn("No se pudo borrar la caché local", e);
  }
  // Firestore quedó cerrado: la app vuelve a empezar desde cero.
  location.replace("/entrar");
}

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [cuenta, setCuenta] = useState<User | null | undefined>(undefined);
  // Se guardan con el uid al que pertenecen: con otra cuenta, valen undefined hasta leerlos.
  const [permisos, setPermisos] = useState<{ uid: string; esSuper: boolean } | null>(null);

  useEffect(
    () =>
      onIdTokenChanged(auth, async (u) => {
        setCuenta(u);
        if (!u) return;
        const esSuper = await u
          .getIdTokenResult()
          .then((t) => t.claims.superadmin === true)
          .catch(() => false);
        setPermisos({ uid: u.uid, esSuper });
      }),
    [],
  );

  const uid = cuenta?.uid ?? null;
  const perfil = useDocumento<Usuario>(uid ? `usuarios/${uid}` : null);
  const membresias = useConsulta<Miembro>(uid ? `membresias-${uid}` : null, () =>
    query(collectionGroup(db, "miembros"), where("uid", "==", uid)),
  );

  const valor: Sesion = {
    cuenta,
    perfil: cuenta === undefined ? undefined : perfil,
    membresias,
    esSuper: cuenta === undefined ? undefined : !cuenta ? false : permisos?.uid === cuenta.uid ? permisos.esSuper : undefined,
    salir: () => cerrarSesion(uid),
  };
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Contexto);
  if (!s) throw new Error("useSesion fuera de ProveedorSesion");
  return s;
}

/** Membresía en un conjunto con el rol aprobado, o null. */
export function rolAprobado(m: Miembro | null | undefined, rol: Rol) {
  const r = m?.roles?.[rol];
  return r && r.estado === "APROBADO" ? r : null;
}

/**
 * Destino de `?volver=`: solo rutas internas de la app. Se interpreta como lo hace el navegador (que convierte "\"
 * en "/" y quita tabs y saltos de línea), así "/\otro.com" no se cuela como ruta propia.
 */
export function rutaDeVuelta(parametros: URLSearchParams, porDefecto = "/inicio"): string {
  const v = parametros.get("volver");
  if (!v?.startsWith("/")) return porDefecto;
  try {
    const u = new URL(v, location.origin);
    return u.origin === location.origin ? u.pathname + u.search + u.hash : porDefecto;
  } catch {
    return porDefecto;
  }
}
